import { useEffect, useState } from 'react';
import { Form, Input, Button, Space, Typography, message, Alert, Spin } from 'antd';
import { MailOutlined, SaveOutlined, InboxOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import LiftlogicResourcePage from './v2/LiftlogicResourcePage';
import useAppSettingsApi from 'hooks/useAppSettingsApi';

const SUPPORT_KEY = 'support_email_config';

const DEFAULT_VALUE = {
  to: '',
  cc: '',
  subjectPrefix: '[PROLIFT Soporte]',
};

const SupportSettingsPage = () => {
  const [form] = Form.useForm();
  const { getSetting, setSetting } = useAppSettingsApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSetting(SUPPORT_KEY);
        if (cancelled) return;
        const val = res?.value && typeof res.value === 'object' ? res.value : DEFAULT_VALUE;
        form.setFieldsValue({
          to: val.to ?? '',
          cc: val.cc ?? '',
          subjectPrefix: val.subjectPrefix ?? DEFAULT_VALUE.subjectPrefix,
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          form.setFieldsValue(DEFAULT_VALUE);
        } else {
          message.error('No pudimos cargar la configuración de soporte.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form, getSetting]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        to: (values.to ?? '').trim(),
        cc: (values.cc ?? '').trim(),
        subjectPrefix: (values.subjectPrefix ?? DEFAULT_VALUE.subjectPrefix).trim(),
      };
      await setSetting(SUPPORT_KEY, payload);
      message.success('Configuración de soporte guardada.');
    } catch (err) {
      if (err?.errorFields) return;
      const status = err?.response?.status;
      if (status === 400) {
        message.error(err?.response?.data?.message || 'Datos inválidos.');
      } else if (status === 401 || status === 403) {
        message.error('No tenés permisos para modificar esta configuración.');
      } else {
        message.error('No pudimos guardar la configuración.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <LiftlogicResourcePage
      title="Soporte"
      toolbar={
        <Space>
          <Link to="/liftlogic/incidents">
            <Button icon={<InboxOutlined />}>Ver incidencias</Button>
          </Link>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={loading}
          >
            Guardar
          </Button>
        </Space>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 720 }}>
          <Alert
            type="info"
            showIcon
            message="Buzón de incidencias"
            description={
              <>
                Los reportes enviados desde el botón <strong>“Reportar un problema”</strong> llegan
                a los emails configurados acá. Para probar el flujo, usá el FloatButton en la
                esquina inferior derecha mientras estás logueado.
              </>
            }
          />
          <Form form={form} layout="vertical" disabled={saving}>
            <Form.Item
              label="Emails destino"
              name="to"
              tooltip="Uno o más, separados por coma. Ej: soporte@tuempresa.com, ops@tuempresa.com"
              rules={[
                { required: true, message: 'Indicá al menos un email' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const list = value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    const invalid = list.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
                    if (invalid.length > 0) {
                      return Promise.reject(
                        new Error(`Email(s) inválido(s): ${invalid.join(', ')}`)
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="soporte@tuempresa.com, ops@tuempresa.com"
              />
            </Form.Item>

            <Form.Item
              label="Emails en copia (CC)"
              name="cc"
              tooltip="Opcional. Mismo formato que arriba."
            >
              <Input prefix={<MailOutlined />} placeholder="jefe@tuempresa.com" />
            </Form.Item>

            <Form.Item
              label="Prefijo del asunto"
              name="subjectPrefix"
              rules={[{ required: true, message: 'El prefijo es obligatorio' }]}
            >
              <Input placeholder="[PROLIFT Soporte]" />
            </Form.Item>
          </Form>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Los cambios aplican inmediatamente al próximo reporte — no requiere deploy.
          </Typography.Text>
        </Space>
      )}
    </LiftlogicResourcePage>
  );
};

export default SupportSettingsPage;
